import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import leadService from '../api/crm/leads/leadService';
import { leadStatusService, leadSourceService } from '../api/crm/config/configService';

// Lead Context for managing lead data
const LeadContext = createContext();

// Lead reducer for state management
const leadReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LEADS':
      return {
        ...state,
        leads: action.payload,
        loading: false
      };
    case 'SET_PAGINATION':
      return {
        ...state,
        pagination: action.payload
      };
    case 'ADD_LEAD':
      return {
        ...state,
        leads: [action.payload, ...state.leads],
        loading: false
      };
    case 'UPDATE_LEAD':
      return {
        ...state,
        leads: state.leads.map(lead =>
          (lead._id === action.payload._id || lead.id === action.payload.id || lead.leadId === action.payload.leadId)
            ? action.payload
            : lead
        ),
        loading: false
      };
    case 'DELETE_LEAD':
      return {
        ...state,
        leads: state.leads.filter(lead =>
          lead._id !== action.payload &&
          lead.id !== action.payload &&
          lead.leadId !== action.payload
        ),
        loading: false
      };
    case 'BULK_DELETE_LEADS':
      return {
        ...state,
        leads: state.leads.filter(lead => !action.payload.includes(lead._id || lead.id || lead.leadId)),
        loading: false
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        loading: false
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };
    case 'SET_LEAD_STATUSES':
      return {
        ...state,
        leadStatuses: action.payload
      };
    case 'SET_LEAD_SOURCES':
      return {
        ...state,
        leadSources: action.payload
      };
    default:
      return state;
  }
};

// Initial state
const initialState = {
  leads: [],
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalLeads: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10
  },
  leadStatuses: [],
  leadSources: []
};

// Lead Context Provider
export const LeadProvider = ({ children }) => {
  const [state, dispatch] = useReducer(leadReducer, initialState);

  // Fetch lead statuses from config master
  const fetchLeadStatuses = useCallback(async () => {
    try {
      const result = await leadStatusService.getAll({ isActive: true });
      if (result.success && result.data) {
        dispatch({ type: 'SET_LEAD_STATUSES', payload: result.data });
      }
    } catch (error) {
      console.error('Error fetching lead statuses:', error);
    }
  }, []);

  // Fetch lead sources from config master
  const fetchLeadSources = useCallback(async () => {
    try {
      const result = await leadSourceService.getAll({ isActive: true });
      if (result.success && result.data) {
        dispatch({ type: 'SET_LEAD_SOURCES', payload: result.data });
      }
    } catch (error) {
      console.error('Error fetching lead sources:', error);
    }
  }, []);

  // Initial data loading
  useEffect(() => {
    fetchLeadStatuses();
    fetchLeadSources();
  }, [fetchLeadStatuses, fetchLeadSources]);

  // Load leads from API - memoized to prevent infinite loops
  const loadLeads = useCallback(async (filters = {}) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      const result = await leadService.getAllLeads(filters);

      if (result.success) {
        if (result.leads && Array.isArray(result.leads)) {
          const mappedLeads = result.leads.map(lead => {
            try {
              const mapped = mapLeadToFrontend(lead);
              if (!mapped) return null;
              return mapped;
            } catch (mapError) {
              return null;
            }
          }).filter(lead => lead !== null);

          dispatch({ type: 'SET_LEADS', payload: mappedLeads });
          dispatch({ type: 'CLEAR_ERROR' });

          if (result.pagination) {
            const backendPagination = result.pagination;
            const normalizedPagination = {
              currentPage: backendPagination.currentPage || backendPagination.page || 1,
              totalPages: backendPagination.totalPages || backendPagination.pages || 1,
              totalLeads: backendPagination.totalLeads || backendPagination.total || 0,
              hasNextPage: backendPagination.hasNextPage !== undefined
                ? backendPagination.hasNextPage
                : (backendPagination.currentPage || backendPagination.page || 1) < (backendPagination.totalPages || backendPagination.pages || 1),
              hasPrevPage: backendPagination.hasPrevPage !== undefined
                ? backendPagination.hasPrevPage
                : (backendPagination.currentPage || backendPagination.page || 1) > 1,
              limit: backendPagination.limit || 10
            };
            dispatch({ type: 'SET_PAGINATION', payload: normalizedPagination });
          }
        } else {
          dispatch({ type: 'SET_LEADS', payload: [] });
        }
      } else {
        dispatch({ type: 'SET_ERROR', payload: result.error || 'Failed to load leads' });
        dispatch({ type: 'SET_LEADS', payload: [] });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message || 'Failed to load leads' });
      dispatch({ type: 'SET_LEADS', payload: [] });
    }
  }, []); // Empty dependency array - function doesn't depend on any props or state

  // NOTE:
  // We intentionally do NOT auto-load leads here.
  // Screens that need leads data should call `loadLeads` explicitly via `useLeads()`.

  // Resolve lead source to a human-readable display name (directly populated from backend)
  const resolveSourceName = (source) => {
    if (!source) return 'Manual Entry';
    if (typeof source === 'object' && source !== null) {
      return source.name || source.label || 'Manual Entry';
    }
    return source;
  };

  // Map backend lead to frontend format
  const mapLeadToFrontend = (lead) => {
    if (!lead) return null;

    return {
      id: lead._id || lead.leadId || lead.id,
      leadId: lead.leadId,
      firstName: lead.firstName,
      lastName: lead.lastName,
      email: lead.email,
      phone: lead.phone,
      alternatePhone: lead.alternatePhone,
      company: lead.company,
      website: lead.website,
      designation: lead.designation,
      source: resolveSourceName(lead.source),
      stage: lead.stage,
      score: lead.score || 0,
      scoreLabel: lead.scoreLabel || 'cold',
      status: mapStageToStatus(lead.stage),
      region: lead.region || lead.address?.state || '',
      tags: lead.tags || [],
      address: lead.address?.street || '',
      city: lead.address?.city || '',
      state: lead.address?.state || '',
      pincode: lead.address?.pincode || '',
      country: lead.address?.country || '',
      businessType: lead.businessType,
      annualRevenue: lead.annualRevenue,
      employeeCount: lead.employeeCount,
      gstNumber: lead.gstNumber,
      drugLicenseNumber: lead.drugLicenseNumber,
      budget: lead.budget,
      leadValue: lead.leadValue || lead.budget || 0,
      timeline: lead.timeline,
      productRequirement: lead.productRequirement,
      notes: lead.notes,
      internalNotes: lead.internalNotes,
      companyPdf: lead.companyPdf,
      companyPdfPublicId: lead.companyPdfPublicId,
      companyDocuments: lead.companyDocuments || [],
      locations: lead.locations || [],
      createdAt: lead.createdAt,
      updatedAt: lead.updatedAt,
      createdBy: lead.createdBy && typeof lead.createdBy === 'object'
        ? `${lead.createdBy.firstName || ''} ${lead.createdBy.lastName || ''}`.trim() || lead.createdBy.email
        : lead.createdBy || 'Unknown',
      assignedTo: lead.assignedTo && typeof lead.assignedTo === 'object'
        ? `${lead.assignedTo.firstName || ''} ${lead.assignedTo.lastName || ''}`.trim() || lead.assignedTo.email
        : lead.assignedTo || 'Unassigned',
      // For backward compatibility
      name: `${lead.firstName || ''} ${lead.lastName || ''}`.trim(),
      owner: lead.assignedTo && typeof lead.assignedTo === 'object'
        ? `${lead.assignedTo.firstName || ''} ${lead.assignedTo.lastName || ''}`.trim() || lead.assignedTo.email
        : (lead.assignedTo || 'Unassigned'),
      ownerEmail: lead.assignedTo && typeof lead.assignedTo === 'object'
        ? lead.assignedTo.email
        : '',
      lastActivity: lead.updatedAt ? formatLastActivity(lead.updatedAt) : 'No activity',
      industry: lead.industry,
      industryId: lead.industry?._id || (typeof lead.industry === 'string' ? lead.industry : ''),
      decisionMaker: lead.decisionMaker,
      decisionMakerId: lead.decisionMaker?._id || (typeof lead.decisionMaker === 'string' ? lead.decisionMaker : ''),
      category: lead.category,
      categoryId: lead.category?._id || (typeof lead.category === 'string' ? lead.category : ''),
      sourceId: lead.source?._id || (typeof lead.source === 'string' ? lead.source : ''),
      assignedToId: lead.assignedTo?._id || (typeof lead.assignedTo === 'string' ? lead.assignedTo : ''),
      originalGenerator: lead.originalGenerator && typeof lead.originalGenerator === 'object'
        ? `${lead.originalGenerator.firstName || ''} ${lead.originalGenerator.lastName || ''}`.trim() || lead.originalGenerator.email
        : lead.originalGenerator || '',
      originalGeneratorId: lead.originalGenerator?._id || (typeof lead.originalGenerator === 'string' ? lead.originalGenerator : ''),
      lostReason: lead.lostReason,
      // Add missing fields
      whatsappPhone: lead.whatsappPhone,
      linkedinProfileUrl: lead.linkedinProfileUrl,
      contactDetailsRaw: lead.contactDetailsRaw,
      businessUnit: lead.businessUnit,
      leadBucket: lead.leadBucket,
      probability: lead.probability,
      nextAction: lead.nextAction,
      lastActiveDate: lead.lastActiveDate,
      lastActiveChannel: lead.lastActiveChannel,
      lastFollowupDate: lead.lastFollowupDate,
      nextFollowupDate: lead.nextFollowupDate,
      followupCadence: lead.followupCadence,
      followupCount: lead.followupCount,
      stagingInstance: lead.stagingInstance,
      revenueMonthly: lead.revenueMonthly,
      revenueOneTime: lead.revenueOneTime,
      revenueType: lead.revenueType,
      costType: lead.costType,
      costOneTime: lead.costOneTime,
      costMrr: lead.costMrr,
      costDurationMonths: lead.costDurationMonths,
      costQuarterly: lead.costQuarterly,
      totalCost: lead.totalCost,
      expectedCloseDate: lead.expectedCloseDate,
      needManagementIntervention: lead.needManagementIntervention,
      leadGenerationDate: lead.leadGenerationDate
    };
  };



  // Map stage to status using dynamic master data
  const mapStageToStatus = (stage) => {
    if (!stage) return 'New';

    // Try to find the status in the master data
    if (state.leadStatuses && state.leadStatuses.length > 0) {
      // Prioritize matching by value first (e.g. stage 'contacted' -> name 'Suspect')
      let status = state.leadStatuses.find(s =>
        s.value && (s.value === stage || s.value.toLowerCase() === stage.toLowerCase())
      );
      if (!status) {
        status = state.leadStatuses.find(s =>
          s.name?.toLowerCase() === stage.toLowerCase()
        );
      }
      if (status) return status.name;
    }

    // Fallback if master data is not yet loaded or not found
    // Just format the slug to a readable name (e.g., 'proposal_sent' -> 'Proposal Sent')
    return stage.charAt(0).toUpperCase() + stage.slice(1).toLowerCase().replace(/_/g, ' ');
  };

  // Format last activity
  const formatLastActivity = (dateString) => {
    if (!dateString) return 'No activity';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    } catch (error) {
      return 'No activity';
    }
  };

  // Create a new lead
  const createLead = async (leadData) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      const result = await leadService.createLead(leadData);

      if (result.success) {
        const mappedLead = mapLeadToFrontend(result.lead);
        dispatch({ type: 'ADD_LEAD', payload: mappedLead });

        // Reload all leads to ensure we have the latest data
        await loadLeads();

        return { success: true, lead: mappedLead };
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
        // Don't set error in context - let the form page handle error display via toast
        // This prevents errors from showing on other pages like LeadListFilters
        return { success: false, error: result.error || 'Failed to create lead' };
      }
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      // Don't set error in context - let the form page handle error display via toast
      return { success: false, error: error.message || 'Failed to create lead' };
    }
  };

  // Create a quick lead with minimal data
  const createQuickLead = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      const quickLeadData = {
        firstName: 'New',
        lastName: 'Lead',
        email: `lead${Date.now()}@example.com`,
        phone: '0000000000',
        company: 'New Company',
        source: 'other',
        notes: 'Auto-generated lead - Created via quick create button'
      };

      const result = await leadService.createLead(quickLeadData);

      if (result.success) {
        const mappedLead = mapLeadToFrontend(result.lead);
        dispatch({ type: 'ADD_LEAD', payload: mappedLead });
        return { success: true, lead: mappedLead };
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
        // Don't set error in context - let the calling page handle error display
        return { success: false, error: result.error || 'Failed to create lead' };
      }
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      // Don't set error in context - let the calling page handle error display
      return { success: false, error: error.message || 'Failed to create lead' };
    }
  };

  // Update an existing lead
  const updateLead = async (leadId, updateData) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      const result = await leadService.updateLead(leadId, updateData);

      if (result.success) {
        const mappedLead = mapLeadToFrontend(result.lead);
        dispatch({ type: 'UPDATE_LEAD', payload: mappedLead });

        // Don't reload all leads here - let the list page handle its own refresh
        // This prevents permission issues when reassigning leads
        dispatch({ type: 'SET_LOADING', payload: false });

        return { success: true, lead: mappedLead };
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
        // Don't set error in context - let the form page handle error display via toast
        // This prevents errors from showing on other pages like LeadListFilters
        return { success: false, error: result.error || 'Failed to update lead' };
      }
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      // Don't set error in context - let the form page handle error display via toast
      return { success: false, error: error.message || 'Failed to update lead' };
    }
  };

  // Delete a lead
  const deleteLead = async (leadId) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      const result = await leadService.deleteLead(leadId);

      if (result.success) {
        dispatch({ type: 'DELETE_LEAD', payload: leadId });
        return { success: true };
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
        // Don't set error in context - let the calling page handle error display via toast
        return { success: false, error: result.error || 'Failed to delete lead' };
      }
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      // Don't set error in context - let the calling page handle error display via toast
      return { success: false, error: error.message || 'Failed to delete lead' };
    }
  };

  // Bulk delete leads
  const bulkDeleteLeads = async (leadIds) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const result = await leadService.bulkDeleteLeads(leadIds);
      if (result.success) {
        dispatch({ type: 'BULK_DELETE_LEADS', payload: leadIds });
        return { success: true, message: result.message };
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
        return { success: false, error: result.error || 'Failed to delete leads' };
      }
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      return { success: false, error: error.message || 'Failed to delete leads' };
    }
  };

  // Bulk assign leads
  const bulkAssignLeads = async (leadIds, assigneeId) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const result = await leadService.bulkAssignLeads(leadIds, assigneeId);
      if (result.success) {
        dispatch({ type: 'SET_LOADING', payload: false });
        return { success: true, message: result.message };
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
        // Don't set error in context - let the calling page handle error display via toast
        return { success: false, error: result.error || 'Failed to assign leads' };
      }
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      return { success: false, error: error.message || 'Failed to assign leads' };
    }
  };

  // Get lead by ID
  const getLeadById = async (leadId) => {
    try {
      const result = await leadService.getLeadById(leadId);
      if (result.success) {
        return mapLeadToFrontend(result.lead);
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  // Get leads by status
  const getLeadsByStatus = (status) => {
    return state.leads.filter(lead => lead.status === status);
  };

  // Get leads by source
  const getLeadsBySource = (source) => {
    return state.leads.filter(lead => lead.source === source);
  };

  // Search leads
  const searchLeads = (query) => {
    if (!query) return state.leads;

    const lowercaseQuery = query.toLowerCase();
    return state.leads.filter(lead =>
      lead.firstName?.toLowerCase().includes(lowercaseQuery) ||
      lead.lastName?.toLowerCase().includes(lowercaseQuery) ||
      lead.email?.toLowerCase().includes(lowercaseQuery) ||
      lead.company?.toLowerCase().includes(lowercaseQuery) ||
      lead.phone?.includes(query)
    );
  };

  // Clear error from context
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const value = {
    ...state,
    createLead,
    createQuickLead,
    updateLead,
    deleteLead,
    bulkDeleteLeads,
    bulkAssignLeads,
    getLeadById,
    getLeadsByStatus,
    getLeadsBySource,
    searchLeads,
    loadLeads,
    clearError
  };

  return (
    <LeadContext.Provider value={value}>
      {children}
    </LeadContext.Provider>
  );
};

// Custom hook to use lead context
export const useLeads = () => {
  const context = useContext(LeadContext);
  if (!context) {
    throw new Error('useLeads must be used within a LeadProvider');
  }
  return context;
};

export default LeadContext;
